import React from 'react';

const G_PRIVACY = 'https://policies.google.com/privacy';
const FIREBASE_PRIVACY = 'https://firebase.google.com/support/privacy';
const APPLE_PRIVACY = 'https://www.apple.com/legal/privacy/';
const SUPPORT_EMAIL = 'tcg-trady@outlook.com';

/** Privacy template aligned with ReRoomy structure; align with actual app data flows before submission. */
export const tcgScannerPrivacyExtended = {
  pageTitle: 'Privacy Policy',
  subtitle: 'TCG Scanner · East IT Services s.r.o.',
  updatedBadge: 'Last updated: June 26, 2026',
  tocTitle: 'Table of Contents',
  tocItems: [
    { id: 's1', label: 'Who We Are' },
    { id: 's2', label: 'Information We Collect' },
    { id: 's3', label: 'How We Use Your Data' },
    { id: 's4', label: 'Camera & Photos' },
    { id: 's5', label: 'Third-Party Services' },
    { id: 's6', label: 'Data Storage & Retention' },
    { id: 's7', label: 'Subscriptions & Payments' },
    { id: 's8', label: 'Your Rights' },
    { id: 's9', label: "Children's Privacy" },
    { id: 's10', label: 'Changes to This Policy' },
  ],
  summary: (
    <>
      <strong>Summary:</strong> TCG Scanner uses your camera and photos only where needed to scan and identify trading
      cards. Collection data and scans you save remain on your device unless you use a feature we clearly disclose as
      cloud-based. Anonymous usage analytics may be sent to help us improve the app. We never sell your personal data.
    </>
  ),
  sections: [
    {
      id: 's1',
      num: '01',
      title: 'Who We Are',
      body: (
        <>
          <p>
            TCG Scanner is a mobile application developed and operated by <strong>East IT Services s.r.o.</strong>{' '}
            (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). The App helps users scan, identify, and manage trading
            card collections; some features may use artificial intelligence powered by Google&apos;s Gemini models or
            other services we integrate.
          </p>
          <p>
            This Privacy Policy explains how we collect, use, and protect your information when you use our
            application. By downloading or using TCG Scanner, you agree to the practices described in this policy.
          </p>
          <p>
            For questions about this policy, you may contact us at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </>
      ),
    },
    {
      id: 's2',
      num: '02',
      title: 'Information We Collect',
      body: (
        <>
          <p>We collect only the minimum information necessary to provide our services.</p>
          <table className="app-legal-doc__data-table">
            <thead>
              <tr>
                <th>Data Type</th>
                <th>What We Collect</th>
                <th>Where It&apos;s Stored</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Photos / Camera</td>
                <td>
                  Images you take or select when scanning trading cards or using visual identification features
                </td>
                <td>On your device unless you explicitly use a cloud or sync feature we disclose in the app</td>
              </tr>
              <tr>
                <td>Collection data</td>
                <td>Card names, sets, quantities, or other details you enter or that the App derives from scans</td>
                <td>Primarily on your device</td>
              </tr>
              <tr>
                <td>Generated outputs</td>
                <td>Identification results, pricing estimates, or other text produced by AI features</td>
                <td>Typically on your device; we do not keep copies on our own servers unless stated otherwise</td>
              </tr>
              <tr>
                <td>Usage Analytics</td>
                <td>Anonymous feature usage events, crash reports, session data</td>
                <td>Our analytics backends (aggregated / anonymised where applicable)</td>
              </tr>
              <tr>
                <td>Subscription Status</td>
                <td>Managed entirely by Apple — we do not access payment details</td>
                <td>Apple servers</td>
              </tr>
            </tbody>
          </table>
          <p>
            We do <strong>not</strong> intentionally collect your name, email address, precise location, contacts, or
            browsing history for advertising. Contact us only if you email us voluntarily.
          </p>
        </>
      ),
    },
    {
      id: 's3',
      num: '03',
      title: 'How We Use Your Data',
      body: (
        <>
          <p>The information we collect is used solely for purposes such as:</p>
          <ul>
            <li>Processing card images through recognition services and returning results to your device</li>
            <li>Helping you build and manage your trading card collection</li>
            <li>Improving scan accuracy, performance, stability, and features</li>
            <li>Understanding which features are used so we can prioritise improvements</li>
            <li>Detecting and fixing technical errors and crashes</li>
            <li>Verifying and managing subscription entitlements via Apple when applicable</li>
          </ul>
          <p>We do not use your content for behavioural advertising or unrelated profiling.</p>
        </>
      ),
    },
    {
      id: 's4',
      num: '04',
      title: 'Camera & Photo Access',
      body: (
        <>
          <p>
            Where features require it, TCG Scanner may request access to your camera or photo library. That access is
            used to:
          </p>
          <ul>
            <li>Capture a photo of a trading card with your camera</li>
            <li>Select existing card photos from your library</li>
            <li>Save scan results or card images to your library when you choose to (with permission)</li>
          </ul>
          <div className="app-legal-doc__highlight">
            <strong>Important:</strong> When a scan or AI feature runs, imagery or other inputs may be transmitted to
            Google (or another provider&apos;s) servers for processing. Please review{' '}
            <a href={G_PRIVACY} target="_blank" rel="noopener noreferrer">
              Google&apos;s Privacy Policy
            </a>{' '}
            (and any other provider we link in-app) regarding how they handle data. Typical outputs are returned to
            your device; we aim not to retain your media on our own infrastructure.
          </div>
          <p>
            You may revoke camera and photo access in Settings. Removing access may limit or disable dependent
            features.
          </p>
        </>
      ),
    },
    {
      id: 's5',
      num: '05',
      title: 'Third-Party Services',
      body: (
        <>
          <p>The App may integrate third-party services, each with its own privacy practices, for example:</p>
          <h3>Google Gemini API</h3>
          <p>
            May be used to process card images you submit and produce identification or related outputs. We recommend
            reviewing{' '}
            <a href={G_PRIVACY} target="_blank" rel="noopener noreferrer">
              Google&apos;s Privacy Policy
            </a>
            .
          </p>
          <h3>Firebase Analytics (Google)</h3>
          <p>
            We may use Firebase Analytics for anonymous, aggregated usage data such as feature usage frequency. Where
            configured, such data should not identify you individually. See{' '}
            <a href={FIREBASE_PRIVACY} target="_blank" rel="noopener noreferrer">
              Firebase Privacy Information
            </a>
            .
          </p>
          <h3>Apple In-App Purchases</h3>
          <p>
            Subscription billing is handled by Apple. We do not receive or store full payment credentials. See{' '}
            <a href={APPLE_PRIVACY} target="_blank" rel="noopener noreferrer">
              Apple&apos;s Privacy Policy
            </a>
            .
          </p>
        </>
      ),
    },
    {
      id: 's6',
      num: '06',
      title: 'Data Storage & Retention',
      body: (
        <>
          <p>
            <strong>On-device data:</strong> Scans, collection entries, and other content you create are primarily stored
            on your device. Delete it via the app, your Photos library, or by uninstalling the app.
          </p>
          <p>
            <strong>Analytics data:</strong> Anonymous analytics may be retained for operational reporting (often up to a
            period similar to 24 months) to observe trends — not to profile named users.
          </p>
          <p>
            <strong>Accounts:</strong> If the App operates without login, there may be no cloud account tied to your
            identity. If we add accounts later, we will update this policy and the app.
          </p>
        </>
      ),
    },
    {
      id: 's7',
      num: '07',
      title: 'Subscriptions & Payments',
      body: (
        <>
          <p>If premium features require a subscription:</p>
          <ul>
            <li>Billing runs through your Apple ID</li>
            <li>You can manage or cancel in Settings → Apple ID → Subscriptions</li>
            <li>We do not have access to your full card details or billing address</li>
            <li>Refund requests go to Apple per their policies</li>
          </ul>
        </>
      ),
    },
    {
      id: 's8',
      num: '08',
      title: 'Your Rights',
      body: (
        <>
          <p>Depending on your location, you may have rights regarding your data, including:</p>
          <ul>
            <li>
              <strong>Access / information:</strong> Ask what categories of data processing apply
            </li>
            <li>
              <strong>Deletion:</strong> Ask us to delete data we hold about you where applicable
            </li>
            <li>
              <strong>Analytics:</strong> You may adjust device analytics participation (e.g. iOS Privacy settings)
            </li>
            <li>
              <strong>GDPR (EU/EEA):</strong> Rights including access, rectification, erasure, restriction, and objection
              where lawful
            </li>
            <li>
              <strong>CCPA (California):</strong> Right to know, delete, and to opt out of sale — we do not sell personal
              data as defined under CCPA
            </li>
          </ul>
          <p>
            Contact us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We typically respond within 30 days.
          </p>
        </>
      ),
    },
    {
      id: 's9',
      num: '09',
      title: "Children's Privacy",
      body: (
        <>
          <p>
            TCG Scanner is not directed at children under 13 (or under the digital consent age applicable in your
            region). We do not knowingly collect personal information from children under that age. Parents or guardians
            who believe a child has shared data should email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
          <p>For users in the European Union, a higher threshold (often 16) or local age of consent may apply.</p>
        </>
      ),
    },
    {
      id: 's10',
      num: '10',
      title: 'Changes to This Policy',
      body: (
        <>
          <p>
            We may update this Privacy Policy. When we do, we will revise the &quot;Last updated&quot; date above.
            Material changes may be communicated in-app or via App Store notes. Continued use of TCG Scanner after
            updates constitutes acceptance where permitted by law.
          </p>
        </>
      ),
    },
  ],
};
